import AddchartIcon from '@mui/icons-material/Addchart';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Grid, IconButton, Stack, Tooltip } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import RGL, { Layout, WidthProvider } from 'react-grid-layout';
import {
  IComposableAnalyticsCharts,
  IComposableChartsWidget,
  IComposableDashboard,
  IComposableDashboardContext,
  IComposableDashboardFiltersContext,
  IComposableDynamicListingContext,
  saveLayout,
  updateDashboard,
} from 'wisestep-api';
import { CardVariant, WsButton, WsCard, WsColors, WsIconCross, WsPageHeader } from 'wisestep-base';

import ErrorLogger from '../../../../../src/providers/ErrorLogger';
import { WsBreadCrumbs } from '../../../../components/WsBreadCrumbs';
import { useWorkspaceContext } from '../../../../context/WorkspaceContext/WorkspaceContext';
import { potentialRevenueTimePeriodList } from '../../../../features/composableAnalytics/DerivedCharts/PotentialRevenue/helper/potentialRevenueDashboardkeys';
import { composableChartsDrawerValues } from '../../../../features/composableAnalytics/helper/composableChartsDrawerKeys';
import {
  getDashBoardBreadCrumbList,
  IDashBoardBreadCrumbTypes,
} from '../../../../features/users/helper/defaultValues';
import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { jobsTimePeriodList } from '../../../composableAnalytics';
import { businessDefinitionTimePeriodList } from '../../../composableAnalytics/DerivedCharts/businessDefinition/helper/postgreBusinessDefinitionKeys';
import { IFreeTextEditorProps } from '../../../composableAnalytics/Widgets/FreeTextEditor';
import { Index } from '../../../composableAnalytics/Widgets/helper/freeTextWidgetKeys';
import getTimePeriodLabelsFromKey from '../../helper/getTimePeriodLabelsFromKey';
import { useComposableDashboardsContext } from '../../hooks/useComposableDashboardsContext';
import { useComposableDashboardsFiltersContext } from '../../hooks/useComposableDashboardsFiltersContext';
import { useComposableDynamicListingContext } from '../../hooks/useComposableDynamicListingContext';
import { useAnalyticsDashboardEditActionStore } from '../../store/useAnalyticsDashboardEditActionStore';

import AddChartSelectionDrawer from './AddChartSelectionDrawer';
import DashboardFilters from './DashboardFilters';
import FilterSelectionDrawer from './FilterSelectionDrawer';
import './rgl-styles.css';
import SaveDialog from './SaveDialog';

const ReactGridLayout = WidthProvider(RGL);

export interface IAnalyticsDashboardViewer {
  requireTitleBanner?: boolean;
}

const AnalyticsDashboardViewer = ({ requireTitleBanner = true }: IAnalyticsDashboardViewer) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const {
    selectedDashboard,
    dashboards,
    setSelectedDashboard,
    setDashboards,
    removeWidget,
    setSelectedDashboardByIndex,
  } = useComposableDashboardsContext() as Required<IComposableDashboardContext>;
  const forUpdate = selectedDashboard.id !== '';
  const [openSaveModal, setSaveModal] = useState(false);
  const [openChartSelectionDrawer, setOpenChartSelectionDrawer] = useState<boolean>(
    forUpdate ? false : true
  );

  const chartsToRender: IComposableAnalyticsCharts[] =
    (selectedDashboard?.charts as IComposableAnalyticsCharts[]) ?? [];
  const [viewMode, setViewMode] = useState(forUpdate ? true : false);
  const setViewAction = useAnalyticsDashboardEditActionStore((state) => state.setViewMode);
  const { addFilterToDashboard, removeFilterFromDashboard, filters, saveAppliedFiltersPayload } =
    useComposableDashboardsFiltersContext() as Required<IComposableDashboardFiltersContext>;
  const [openFilterSelectionDrawer, setOpenFilterSelectionDrawer] = useState(false);
  const currentLayout = useRef<Layout[]>([]);
  const workspaceContext = useWorkspaceContext();
  const {
    businessDefinitionChartsKeysPostgre,
    potentialRevenueForBDefChartsKeys,
    leadByStatusChartsKeys,
  } = useComposableDynamicListingContext() as Required<IComposableDynamicListingContext>;

  const businessDefinitionChartsKeysPostgreMemo = useMemo(
    () =>
      businessDefinitionTimePeriodList.flatMap((timePeriod) =>
        businessDefinitionChartsKeysPostgre(timePeriod)
      ),
    [businessDefinitionChartsKeysPostgre]
  );
  const potentialRevenueForBDefChartsKeysMemo = useMemo(
    () =>
      potentialRevenueTimePeriodList.flatMap((timePeriod) =>
        potentialRevenueForBDefChartsKeys(timePeriod)
      ),
    [potentialRevenueForBDefChartsKeys]
  );
  const leadByStatusChartsKeysMemo = useMemo(
    () => jobsTimePeriodList.flatMap((timePeriod) => leadByStatusChartsKeys(timePeriod)),
    [leadByStatusChartsKeys]
  );

  const handleNotFoundCase = (chartKey: string) => {
    const updatedCharts = chartsToRender.filter((chartKeyObj) => chartKeyObj.type !== chartKey);
    setSelectedDashboard((prev) => ({ ...prev, charts: updatedCharts }));
  };

  useEffect(() => {
    setViewAction(!viewMode);
  }, [viewMode]);

  const generateChart = ({
    type: chartKey,
    layout,
    text,
    color,
    id: widgetId = '',
  }: IComposableAnalyticsCharts) => (
    <Grid
      key={widgetId}
      container
      data-grid={layout}
      p={1}
      bgcolor={color ?? 'white'}
      overflow="hidden"
      borderRadius={1}
      boxShadow="0px 2px 4px rgba(0, 0, 0, 0.1)"
    >
      {!viewMode && (
        <Box
          className="drag-handle"
          style={{
            cursor: 'grabbing',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 1,
            width: 'calc(100% - 4rem)',
            height: 22,
          }}
        />
      )}

      {chartKey !== Index.FREE_TEXT_WIDGET && (
        <Tooltip title={getTimePeriodLabelsFromKey(chartKey, filters, text)} placement="left">
          <InfoIcon
            sx={{
              position: 'absolute',
              top: '0.4rem',
              right: '1.7rem',
              zIndex: 2,
              color: WsColors.TertiaryShades300,
            }}
            fontSize="small"
          />
        </Tooltip>
      )}
      {!viewMode && (
        <IconButton
          sx={{ position: 'absolute', top: 0, right: 0, zIndex: 2 }}
          onClick={() => removeWidget({ id: widgetId, type: chartKey } as IComposableChartsWidget)}
        >
          <WsIconCross />
        </IconButton>
      )}

      <Grid item xs={12} height="95%">
        {(() => {
          const Widget = [
            ...composableChartsDrawerValues,
            ...businessDefinitionChartsKeysPostgreMemo,
            ...potentialRevenueForBDefChartsKeysMemo,
            ...leadByStatusChartsKeysMemo,
          ].find((chartKeyObj) => chartKeyObj.type === chartKey)?.component;

          if (!Widget) {
            handleNotFoundCase(chartKey);
            return null;
          }
          if (chartKey === Index.FREE_TEXT_WIDGET) {
            const FreeTextEditor = Widget as React.FC<IFreeTextEditorProps>;
            return <FreeTextEditor text={text} id={widgetId} dashboardEditMode={!viewMode} />;
          } else return Widget;
        })()}
      </Grid>
    </Grid>
  );

  const mapNewLayoutToChartsBeforeSave = (newDashboard: IComposableDashboard) => {
    const charts = selectedDashboard.charts as IComposableAnalyticsCharts[];
    currentLayout.current.forEach((layoutObj) => {
      const targetChartIndex = charts.findIndex((chart) => chart.id === layoutObj.i);
      if (targetChartIndex !== -1)
        charts.splice(targetChartIndex, 1, {
          ...charts[targetChartIndex],
          layout: {
            ...(charts[targetChartIndex].layout as Record<string, any>),
            x: layoutObj.x,
            y: layoutObj.y,
            w: layoutObj.w,
            h: layoutObj.h,
          },
        });
    });
    setSelectedDashboard((prev) => ({
      ...prev,
      charts,
      name: newDashboard?.name,
      description: newDashboard?.description,
    }));
  };

  const saveDashboard = async (newDashboard: IComposableDashboard) => {
    mapNewLayoutToChartsBeforeSave(newDashboard);
    try {
      if (!forUpdate) {
        const response = await saveLayout({
          ...newDashboard,
          charts: JSON.stringify(selectedDashboard.charts),
          appliedDashboardFilters: saveAppliedFiltersPayload && saveAppliedFiltersPayload(),
          accessLevel: 'restricted',
        });
        setDashboards((prev) => {
          prev.push(response.data);
          return [...prev];
        });
        setSelectedDashboardByIndex(dashboards.length - 1);
        setNotification(success('New Dashboard Created Successfully!'));
      } else {
        const response = await updateDashboard({
          ...newDashboard,
          charts: JSON.stringify(selectedDashboard.charts),
          appliedDashboardFilters: saveAppliedFiltersPayload && saveAppliedFiltersPayload(),
        });
        setDashboards((prev) => {
          const updatedDashboards = [...prev];
          const targetIndex = updatedDashboards.findIndex(
            (dashboard) => dashboard.id === newDashboard.id
          );
          if (targetIndex !== -1) {
            updatedDashboards[targetIndex] = response.data;
          }
          return updatedDashboards;
        });
        setNotification(success('Updated Dashboard Successfully!'));
      }
    } catch (e) {
      setNotification(failed('Some error occurred. Please try later'));
    }
    setSaveModal(false);
    setViewMode(true);
  };

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <Grid container spacing={2}>
        <Grid item xs={12} pb={2}>
          {requireTitleBanner && (
            <>
              <WsBreadCrumbs
                breadCrumbsList={getDashBoardBreadCrumbList(
                  IDashBoardBreadCrumbTypes.YOUR_DASHBOARDS,
                  !isEmpty(selectedDashboard?.name) ? selectedDashboard?.name : 'Create DashBoard'
                )}
              />
              <Grid item xs={12} pb={2}>
                <WsPageHeader
                  title={selectedDashboard?.name ?? 'Title not defined'}
                  rightChildren={
                    viewMode && (
                      <WsButton
                        disabled={
                          selectedDashboard.createdBy == 'Default' && !workspaceContext.owns
                            ? true
                            : false
                        }
                        onClick={() => {
                          setOpenChartSelectionDrawer(true);
                          setViewMode(false);
                        }}
                      >
                        Edit
                      </WsButton>
                    )
                  }
                />
              </Grid>
            </>
          )}
          {!viewMode && (
            <Stack direction="row" p={2} justifyContent="end" spacing={2}>
              <WsButton onClick={() => setSaveModal(true)}>Save</WsButton>
              <WsButton onClick={() => setViewMode(true)}>Cancel</WsButton>
              <WsButton onClick={() => setOpenFilterSelectionDrawer(true)}>Add Filters</WsButton>
              <WsButton onClick={() => setOpenChartSelectionDrawer(true)}>
                Add more widgets
              </WsButton>
            </Stack>
          )}
          <DashboardFilters />
          {chartsToRender.length > 0 ? (
            <Box mx={-1}>
              <ReactGridLayout
                className="layout"
                draggableHandle=".drag-handle"
                rowHeight={150}
                isDraggable={!viewMode}
                isResizable={!viewMode}
                onDragStop={(newLayout) => (currentLayout.current = newLayout)}
                onResizeStop={(newLayout) => (currentLayout.current = newLayout)}
              >
                {chartsToRender?.map((chart) => generateChart(chart))}
              </ReactGridLayout>
            </Box>
          ) : (
            <Grid mt={4}>
              <WsCard
                cardType={CardVariant.WITH_OUT_BG}
                title="Compose Your Dashboards"
                description="Start adding charts from different indexes"
                middleComponent={
                  <AddchartIcon sx={{ color: WsColors.TheBlues500, fontSize: 100 }} />
                }
                isButton={true}
                buttonText="Add Widgets"
                btnStartIcon={<AddchartIcon />}
                onClick={() => {
                  setOpenChartSelectionDrawer(true);
                  setViewMode(false);
                }}
                buttonSize="large"
                sx={{ height: '70vh', p: '10vw' }}
              />
            </Grid>
          )}
        </Grid>
        {openChartSelectionDrawer && (
          <AddChartSelectionDrawer
            openChartSelectionDrawer={openChartSelectionDrawer}
            setOpenChartSelectionDrawer={setOpenChartSelectionDrawer}
            workspaceId={selectedDashboard.workspaceId}
          />
        )}
        {openFilterSelectionDrawer && (
          <FilterSelectionDrawer
            openFilterSelectionDrawer={openFilterSelectionDrawer}
            setOpenFilterSelectionDrawer={setOpenFilterSelectionDrawer}
            onAdd={addFilterToDashboard}
            onRemove={removeFilterFromDashboard}
          />
        )}
        {openSaveModal && (
          <SaveDialog
            setSaveModal={setSaveModal}
            saveDashboard={saveDashboard}
            update={forUpdate}
          />
        )}
      </Grid>
    </ErrorBoundary>
  );
};

export default AnalyticsDashboardViewer;
